import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { conversionCount, sharePercent } from '../format';
import { useReportWidget } from '../hooks/useReportWidget';
import { COLORS, cardSx } from '../theme';
import { ROWS_PER_PAGE_OPTIONS, paginateRows } from './Conversions';
import { googleAdsRows } from './GoogleAdsCampaignBreakdownCard';
import { pageWindow } from './ImpressionsByType';
import WidgetSkeleton from './WidgetSkeleton';

const KEY = 'googleAdsConversionActions';

// Same 20/50/100 toggle styling Conversions.tsx/ImpressionsByType.tsx/
// PublisherInventoryTable.tsx each keep their own copy of, rather than a
// shared component.
const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

/** Rows/Prev-Next footer, same look/feel as every other paginated Reporting V2
 * grid (flank#5149 follow-up). */
const PaginationFooter = ({
  total,
  rowsPerPage,
  safePage,
  pageCount,
  shown,
  onPageChange,
  onRowsPerPageChange,
}: {
  total: number;
  rowsPerPage: number;
  safePage: number;
  pageCount: number;
  shown: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}) => {
  if (total <= ROWS_PER_PAGE_OPTIONS[0]) return null;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pt: 1 }}
    >
      <Typography variant='caption' sx={{ color: COLORS.muted }}>
        {(safePage * rowsPerPage + 1).toLocaleString()}–{(safePage * rowsPerPage + shown).toLocaleString()} of{' '}
        {total.toLocaleString()} rows
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant='caption' sx={{ color: COLORS.muted }}>
            Rows
          </Typography>
          <ToggleButtonGroup
            size='small'
            exclusive
            value={rowsPerPage}
            onChange={(_e, v) => v && onRowsPerPageChange(v)}
            sx={darkToggleSx}
          >
            {ROWS_PER_PAGE_OPTIONS.map(n => (
              <ToggleButton key={n} value={n}>
                {n}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size='small'
            disabled={safePage === 0}
            onClick={() => onPageChange(Math.max(0, safePage - 1))}
            sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
          >
            Prev
          </Button>
          {pageWindow(safePage, pageCount).map((n, i) =>
            n === -1 ? (
              <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                …
              </Typography>
            ) : (
              <Button
                key={n}
                size='small'
                onClick={() => onPageChange(n)}
                sx={{
                  textTransform: 'none',
                  minWidth: 32,
                  px: 1,
                  color: n === safePage ? '#fff' : COLORS.muted,
                  bgcolor: n === safePage ? COLORS.black : 'transparent',
                  '&:hover': { bgcolor: n === safePage ? '#000' : COLORS.pageBg },
                }}
              >
                {n + 1}
              </Button>
            ),
          )}
          <Button
            size='small'
            disabled={safePage >= pageCount - 1}
            onClick={() => onPageChange(Math.min(pageCount - 1, safePage + 1))}
            sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * flank#4009 — delivery rows carry no action (NULL), so they come back as one
 * unnamed group with zero conversions; drop it rather than render a blank row.
 */
export const namedActions = (rows: ReportRow[]): ReportRow[] =>
  googleAdsRows(rows).filter(
    row => row.conversion_action !== null && row.conversion_action !== undefined && row.conversion_action !== '',
  );

const GoogleAdsConversionActionsCard = () => {
  const {
    rows: fetchedRows,
    loading,
    error,
  } = useReportWidget(KEY, {
    dimensions: ['dsp', 'conversion_action'],
    metrics: ['conversions'],
    sort: [{ field: 'conversions', dir: 'desc' }],
    limit: 500,
    dspsOverride: ['googleads'],
  });
  const rows = namedActions(fetchedRows);
  const total = rows.reduce((sum, row) => sum + Number(row.conversions ?? 0), 0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  const { pageRows, pageCount, safePage } = paginateRows(rows, page, rowsPerPage);

  return (
    <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
        Google Ads — All Conversions by Action
      </Typography>

      {loading ? (
        <WidgetSkeleton height={280} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No Google Ads conversions for the selected range yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: COLORS.muted, fontWeight: 600 }}>Conversion Action</TableCell>
                <TableCell align='right' sx={{ color: COLORS.muted, fontWeight: 600 }}>
                  All Conversions
                </TableCell>
                <TableCell align='right' sx={{ color: COLORS.muted, fontWeight: 600 }}>
                  Share
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map(row => (
                <TableRow key={String(row.conversion_action)}>
                  <TableCell>{String(row.conversion_action)}</TableCell>
                  <TableCell align='right'>{conversionCount(row.conversions)}</TableCell>
                  <TableCell align='right'>{sharePercent(row.conversions, total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <PaginationFooter
        total={rows.length}
        rowsPerPage={rowsPerPage}
        safePage={safePage}
        pageCount={pageCount}
        shown={pageRows.length}
        onPageChange={setPage}
        onRowsPerPageChange={next => {
          setRowsPerPage(next);
          setPage(0);
        }}
      />
    </Box>
  );
};

export default GoogleAdsConversionActionsCard;
